import { useRef, useState, useSyncExternalStore } from 'react';
import { X, Globe, Lock } from '../../icons';
import { createProject, type TaskProjectDto } from '../../api/tasks';
import type { TeamDto } from '../../api/project-teams';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './ProjectCreateSimpleModal.css';

interface ProjectCreateSimpleModalProps {
  teams: TeamDto[];
  /** 진입 맥락의 팀 — 팀 화면에서 열면 그 팀이 미리 선택된다. */
  initialTeamId: string | null;
  isLeaving?: boolean;
  onClose: () => void;
  onCreated: (project: TaskProjectDto, teamId: string | null) => void;
}

/** 무소속(개인) 센티널 — 빈 문자열을 값으로 쓰지 않기 위해. */
/*
 * ⛔ 2026-10-12 — 「개인 (스페이스 없음)」을 걷었다.
 *
 * ```
 * 사용자 결정 ⇒ 「프로젝트는 ★ 무조건 스페이스가 있어야한다.」
 * ⇒ 49차에 운영 데이터 이관이 끝났다 ⇒ 운영 무소속 0 / 16(관제 실측)
 *   (만든 「개인 스페이스」 id 22·23·24 ⇒ user 4·9·10)
 * ⇒ ⇒ 그래서 이 판은 «새로 생기는 길»을 막는다. 기존 데이터는 건드리지 않는다(마이그는 51차).
 * ```
 *
 * ⚠ 스페이스가 «하나도 없는» 사람은 프로젝트를 못 만든다 ⇒ 그래서 그때는
 *   선택 대신 「스페이스를 먼저 만들어 주세요」를 보이고 [새 스페이스 만들기]로 이어 준다.
 * ⚠ 서버도 teamId 를 required 로 막는다(projects.routes.ts) — 화면만 막으면
 *   API 직접 호출로 다시 생긴다.
 * ⚠⚠ 반영 순서 ⇒ «프론트 먼저». 서버가 먼저 거부하면 옛 번들에서 그 선택지를 고른
 *   사용자가 400 을 받는다. 프론트가 먼저면 그 선택지가 사라지고 서버는 아직 받아 주니
 *   깨지는 창이 «없다».
 */
const NO_SPACE = '';   // ⇐ 고르지 않은 상태(옛 PERSONAL 자리)

/**
 * 사용자가 고르는 **색 12개** — ⛔ 테마 색이 «아니다».
 *
 * ## ★ 왜 인라인 hex 인데 괜찮은가
 *
 * CLAUDE.md 의 「인라인 hex 금지」는 **테마 색**(배경·글자·테두리)에 대한 규율이다. 이 열두 개는
 * **사용자가 고르는 «데이터»**이고 DB `task_projects.color` 에 그 글자가 그대로 들어간다.
 * ⇒ ★ main 도 같은 꼴을 쓴다 — `ProjectTopBar.STRIP_COLORS` · `dashboardStore.BOARD_COLORS`.
 *
 * ## ⛔ 왜 `TaskGroupCreateModal` 에서 가져오지 않나
 *
 * 정본(`origin/feature/dev-syk`)은 `import { GROUP_COLORS } from './TaskGroupCreateModal'` 로
 * 가져온다. ⇒ ⛔ 그런데 **그 파일이 main 에 «없다»**(2026-10-04 실측: `TaskGroupCreateModal.tsx`
 * 0건 · `GROUP_COLORS` 0건). 그 모달은 **다른 조각**이라 색 목록 하나 때문에 통째로 끌어오지
 * 않는다(설계 문서 ④ 「범위 — 넓히지 않는다」).
 * ⇒ ⚠ **그 조각이 오는 날** 이 상수와 그쪽 `GROUP_COLORS` 가 **두 벌**이 된다.
 *   ⇒ ★ 그때 **한쪽을 지우고 import 로 바꾼다.** 값은 정본과 **글자까지 같게** 두었으니
 *     (아래 열두 개 · 2026-10-04 대조) 그 합치기는 **지우기 한 번**이다.
 *
 * ### ⛔⛔ 2026-10-10 정정 — ★ 그 조각이 ⭐ «왔다». 그리고 ★ 「지우기 한 번」이 ⭐ 틀렸다
 *
 * ```
 * ⇒ PR ②-b 가 ★ pages/ProjectPage/TaskGroupCreateModal.tsx 를 ⭐ 정본 그대로 들였다
 *    (blob ff9545eeed73 ＝ 정본 · 그 파일이 ★ GROUP_COLORS 를 ⭐ export 한다)
 * ⇒ ⛔ 그런데 ★ 「두 벌」이 아니라 ⇒ ⭐ «세 벌»이었다. ⇒ ⇒ 실측(2026-10-10):
 *      ① pages/ProjectPage/TaskGroupCreateModal.tsx    ⇐ 이 판이 들인 것 (정본 ff9545eeed73)
 *      ② pages/ProjectV2/TaskGroupCreateModal.tsx      ⇐ ★ «이미 있었다» · blob d1ad1ed61a96
 *                                                         ⇒ ⇒ ⛔ 정본과 ★ 다르다(우리가 손본 벌)
 *      ③ 이 파일                                        ⇐ 제 손으로 들고 ＋ ⭐ `export` 한다
 * ⇒ ⇒ ⛔ 그리고 ★ `ProjectCreateSimpleModal.contract.test.tsx` 가 ⇒ ⭐ ③ 에서 가져간다
 *      (`import { ProjectCreateSimpleModal, GROUP_COLORS } from './ProjectCreateSimpleModal'`
 *       ⇒ 그 시험이 ★ 코드 ⭐ 6 자리에서 쓴다 — `[8]`·`[0]`·길이 12·7자 꼴)
 * ⇒ ⇒ ⇒ ★ 즉 ⭐ 「지우기 한 번」이 아니라 ⇒ ★ 「어느 벌이 ⭐ SSOT 인가」를 ★ 먼저 정해야 한다
 *       ⇒ ⇒ 그 결정은 ★ ㉤(라우터 전환 ⇒ ②가 죽는 날) ⭐ 뒤라야 뜻이 선다.
 * ⇒ ✅ 그래서 ★ PR ②-b 는 ⭐ 합치지 «않고» ⇒ ★ 「세 벌의 값이 ⭐ 갈리지 않는다」를 잠갔다
 *      (`canon-layer2.guard.test.ts` ④ — ⇒ 셋을 ★ 글자까지 맞대고 ⭐ 7자까지 본다)
 * ⇒ ⚠ ★ 내 ⏳ 쪽지가 ⭐ 틀린 까닭 ⇒ ⇒ 「두 벌이 된다」를 쓸 때 ★ «세는 범위»를 ⭐ ProjectPage/ 로만
 *      보았다 — ⇒ ⇒ ★ ProjectV2/ 에 ⭐ 이미 한 벌이 있는 것을 ★ 안 셌다.
 *      ⇒ ⇒ ⇒ ⭐ 교훈 ⇒ ★ 「N 벌이 된다」는 ⇒ ⭐ «레포 전체»로 세고 ★ 그 ref 를 적는다.
 * ⏳ 만료 ⇒ ★ 세 벌이 ⭐ «하나»가 되는 날 ⇒ ⇒ 이 단락과 그 가드 묶음을 ★ 함께 지워라.
 * ```
 *
 * ## ✅ 7자다 — 서버가 그것만 받는다
 *
 * 전부 `#RRGGBB` **7자**이고, 서버의 새 스키마가 `^#[0-9a-fA-F]{6}$` 로 못 박았다
 * (`projects.routes.ts:82` · 사용자 결정 2026-10-04). ⇒ ⛔ 8자(`#RRGGBBAA`)를 넣으면 **400** 이다.
 */
export const GROUP_COLORS = [
  '#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6', '#8b5cf6', '#a855f7',
  '#ec4899', '#f472b6', '#f59e0b', '#f97316', '#64748b', '#10b981',
] as const;

/**
 * ProjectCreateSimpleModal (화면구성도 V0.1 "새 프로젝트 생성(제안)", 2026-08-18).
 *
 * 지적은 "신규 프로젝트 생성 시 화면이 너무 복잡하고 사용자가 어지럽다" 였다.
 * 기존 모달은 [기본 정보 / 소속·일정 / …] 섹션에 이름·링크·설명·팀·마감일·빠른 기간 버튼까지
 * 담아 스크롤이 생겼다. 제안대로 **한 화면에 세 가지만** 남긴다:
 *   ① 색상 및 프로젝트명  ② 프로젝트 위치(팀/개인)  ③ 프로젝트 유형(공개/비공개)
 *
 * 뺀 값들(설명·마감일·제목 링크)은 만든 뒤 **프로젝트 설정**에서 채운다 — 생성 순간에
 * 반드시 필요한 값이 아니라서, 여기 두면 첫 화면부터 사람을 붙잡는다.
 *
 * 기존 `ProjectCreateModal` 은 보존한다(소스 회귀 금지) — props 시그니처를 같게 맞춰 뒀으니
 * 롤백은 호출부의 import 한 줄만 되돌리면 된다.
 *
 * ## ⛔⛔ 이 모달은 2026-10-04 «전»에는 지을 수 없었다 — 서버가 두 칸을 버렸다
 *
 * ①의 «색»과 ③의 «유형»을 `POST /projects` 가 받지 않았고, 이 레포의 Fastify ajv 는
 * `removeAdditional` 이라 **400 도 없이 조용히 버렸다**. ⇒ 저장은 성공(200)하고 색도 공개범위도
 * 안 들어가는 — 「고장보다 나쁜」 화면이 됐을 것이다(설계 문서 `project-create-simple-modal-
 * server-ask-2026-10-04.md`).
 * ⇒ ✅ PR #2161 이 그 셋을 세웠다(㉮ 스키마 `:82-83` · ㉯ create 의 data `:368-369` ·
 *   ㉰ select·DTO `:371`·`:151-152`). ⇒ ★ 그래서 **이제** 짓는다.
 */
export function ProjectCreateSimpleModal({
  teams, initialTeamId, isLeaving = false, onClose, onCreated,
}: ProjectCreateSimpleModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(GROUP_COLORS[8]);   // 시안의 노란 스와치
  const [paletteOpen, setPaletteOpen] = useState(false);
  // ⇒ 스페이스를 «반드시» 고른다 ⇒ 넘겨받은 것 → 첫 스페이스 → (없으면) 빈 값
  const [location, setLocation] = useState<string>(initialTeamId ?? teams[0]?.id ?? NO_SPACE);
  const [visibility, setVisibility] = useState<'TEAM' | 'PRIVATE'>('TEAM');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEscapeClose(onClose, submitting);

  async function submit() {
    const n = name.trim();
    /*
     * ⚠ `TeamCreateInviteModal` 은 ★ 아직 ⭐ «우리 트리에 없다»(2026-10-10 실측) —
     *   ⇒ 정본 `origin/feature/dev-syk` 에 있고 ⇒ ★ 들이면 ⭐ «팀 만들기 흐름»이 바뀌므로
     *     ⇒ ⇒ ★ 사용자 결정이 ⭐ 필요하다(판 F-2 로 미뤘다).
     * ⇒ ⭐ 그러니 ★ 아래 규칙의 «본»은 ⇒ ⭐ 그 파일이 아니라 ★ ⭐ «이 자리와 TaskGroupCreateModal» 이다.
     *   ⇒ ⭐ 규칙 자체 ⇒ ★ 「⭐ 비활성 버튼 대신 ⇒ ★ 까닭을 말한다」.
     * ⏳ 만료 조건 ⇒ ★ 그 파일이 들어오면 ⇒ ⭐ 이 글을 지우고 ★ 그 이름을 ⭐ 되살려라.
     * ⛔ 2026-10-10 정정 — ★ 위 두 줄이 ⭐ 두 군데 ★ 좁다:
     *   ⓐ 「우리 트리에 없다」 ⇒ ★ ⭐ «일부»만 맞다 — ⇒ `TeamCreateInviteModal.css` 는
     *      ⇒ ⇒ ★ wc -l 241 줄이 ⭐ «이미 있다»(판 F-1) ⇒ ⇒ `TeamInvitePickerModal.tsx:16` 이
     *         ⇒ ⇒ ★ 그것을 ⭐ import 한다. ⇒ ⇒ ⛔ 없는 것은 ★ ⭐ «.tsx» 쪽뿐이다.
     *      ⇒ ⛔ 2026-10-10 ★ 두 번째 정정 — ⇒ ⭐ 그 .tsx 도 ★ 이제 «있다»
     *         ⇒ ★ 「정본 프로젝트 메뉴를 ⭐ 그대로 이관」(사용자 2026-10-10)의 ★ 첫 판에서
     *            ⇒ ⇒ ⭐ `pages/ProjectPage/TeamCreateInviteModal.tsx` 를 ★ 정본 글자로 들였다.
     *         ⇒ ⇒ ⚠ 다만 ★ «그리는 곳»은 ⭐ 아직 0 이다 — ⇒ 아래 규칙의 «본»은 ★ 그대로
     *            ⇒ ⇒ ⭐ «이 자리와 TaskGroupCreateModal» 이다.
     *         ⇒ ⇒ ⇒ ★ 자세한 것은 ⭐ `TeamsDetailPage.tsx` 의 ★ 「판 F-2」 쪽지.
     *   ⓑ ⏳ 「그 파일이 들어오면」 ⇒ ★ 그것은 ⭐ «파일 이름» 꼴이다 — ⇒ ⛔ 위 ⓐ 처럼 깨진다
     *      ⇒ ⇒ ★ 상태가 ⭐ «없다 / 일부 / 전부» ★ 셋이기 때문이다.
     *      ⇒ ⇒ ✅ 바른 꼴 ⇒ ⭐ 「`TeamCreateInviteModal` «이라는 이름»을 내놓는 모듈이 생기고
     *         ⇒ ⇒ ★ `TeamsDetailPage` 가 ⭐ 그것을 «그리는» 날」.
     *   ⇒ ⭐ 막는 것 ★ «전부»와 그 까닭은 ⇒ ★ `TeamsDetailPage.tsx` 의 ⭐ 「판 F-2」 쪽지에 있다.
     * ✅ 2026-10-13 — 위 ⓑ 의 만료 조건이 섰다: 정본 이관 판 2 에서 `TeamsDetailPage` 가
     *   `TeamCreateInviteModal` 을 그린다(「판 F-2」 쪽지는 그 자리에서 걷었다).
     */
    // 비활성 버튼 대신 이유를 말한다.
    if (!n) {
      setError(tProject('project.create.err_name'));
      nameRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setError(null);
    const teamId = location === NO_SPACE ? null : location;
    if (teamId === null) {
      // ⇒ 단추가 이미 꺼져 있지만, 키보드·자동화로 올 수 있어 한 겹 더 막는다.
      setError(tProject('project.create.need_space'));
      return;
    }
    try {
      /*
       * ★ 색과 유형을 **늘 보낸다** — 정본 그대로다. 색은 고르지 않아도 기본값(노란 스와치)이 선다.
       *
       * ⚠⚠ **한때 「개인이면 visibility 를 안 보낸다」로 바꿨다가 되돌렸다** (2026-10-04).
       *   까닭: 화면은 개인일 때 그 칸을 `disabled` 로 두고 「생성자만 봅니다」라고 말하는데
       *   'TEAM' 을 보내면 화면과 다른 값이 들어간다고 보았다. ⇒ ⛔ **그런데 재 보니 효과가 0 이다**:
       *   표가 `visibility NOT NULL DEFAULT 'TEAM'` 이라(실측) **안 보내도 'TEAM' 이 들어간다.**
       *   로컬 실측: `task_projects` 4행 전부 `visibility='TEAM'` · `teamId` 는 전부 NULL
       *   ⇒ 즉 **개인 프로젝트가 이미 TEAM 으로 저장돼 있다.**
       *   ⇒ ★ 그러므로 이것은 **화면이 고칠 수 있는 것이 아니다** — 「개인 프로젝트의 공개 범위가
       *     무엇이어야 하나」는 ⭐ 서버·사용자 결정 사안이다(설계 문서 ⑤ 의 그 줄). 보고로 올렸다.
       *   ⇒ ⚠ 그리고 효과 0 인 차이를 정본에 넣으면 **사보타주로도 안 갈린다** — 이 레포의
       *     「미래를 위한 선택은 소스 불변식으로」가 말하는 그 자리라, 차이를 **없애는 쪽**을 골랐다.
       */
      const project = await createProject({
        name: n,
        color,
        visibility,
        ...(teamId ? { teamId } : {}),
      });
      onCreated(project, teamId);
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tProject('project.create.err_save')}`);
      setSubmitting(false);
    }
  }

  return (
    <div
      className={`pj-modal-backdrop${isLeaving ? ' pj-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting && !isLeaving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.create.simple_title')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`pj-modal pcs-modal${isLeaving ? ' pj-modal--leaving' : ''}`}>
        <header className="pcs-head">
          <h2>{tProject('project.create.simple_title')}</h2>
          <button type="button" className="pcs-x" onClick={onClose} disabled={submitting} aria-label={tProject('project.btn.close')} data-tip={tProject('project.btn.close')}>
            <X size={16} />
          </button>
        </header>

        <label className="pcs-label" htmlFor="pcs-name">{tProject('project.create.color_and_name')}</label>
        <div className="pcs-namerow">
          <div className="pcs-swatch-wrap">
            <button
              type="button"
              className="pcs-swatch"
              style={{ background: color }}
              aria-label={tProject('project.create.color_pick')}
              aria-expanded={paletteOpen}
              onClick={() => setPaletteOpen((v) => !v)}
            />
            {paletteOpen && (
              <div className="pcs-palette" role="radiogroup" aria-label={tProject('project.create.color_pick')}>
                {GROUP_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={c === color}
                    aria-label={c}
                    className={`pcs-palette-dot${c === color ? ' pcs-palette-dot--on' : ''}`}
                    style={{ background: c }}
                    onClick={() => { setColor(c); setPaletteOpen(false); }}
                  />
                ))}
              </div>
            )}
          </div>
          <input
            id="pcs-name"
            ref={nameRef}
            className="pcs-input"
            aria-invalid={error != null && !name.trim()}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // 이름을 채우면 '이름을 입력하세요' 안내는 거둔다(2026-09-11) — 테두리만 풀리고
              // 문구가 남아 있으면 아직 틀린 것처럼 읽힌다. 서버 오류 문구는 그대로 둔다.
              if (error === tProject('project.create.err_name') && e.target.value.trim()) setError(null);
            }}
            placeholder={tProject('project.create.name_ph')}
            maxLength={100}
            autoFocus
            disabled={submitting}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
          />
        </div>

        <label className="pcs-label pcs-label--mt" htmlFor="pcs-location">{tProject('project.create.location')}</label>
        <select
          id="pcs-location"
          className="pcs-select"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          disabled={submitting}
        >
          {teams.length === 0 && <option value={NO_SPACE}>{tProject('project.create.need_space')}</option>}
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {teams.length === 0 && (
          // ⇒ 스페이스가 없으면 «여기서 막히지 않게» 길을 준다(홈의 그 단추와 같은 글자).
          <p className="pcs-hint" role="status">{tProject('project.create.need_space_hint')}</p>
        )}

        <label className="pcs-label pcs-label--mt" htmlFor="pcs-visibility">{tProject('project.create.visibility')}</label>
        <select
          id="pcs-visibility"
          className="pcs-select"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as 'TEAM' | 'PRIVATE')}
          disabled={submitting}
        >
          <option value="TEAM">{tProject('project.create.vis_team')}</option>
          <option value="PRIVATE">{tProject('project.create.vis_private')}</option>
        </select>
        <p className="pcs-hint">
          {visibility === 'TEAM'
            ? <><Globe size={11} aria-hidden /> {tProject('project.create.vis_hint_team')}</>
            : <><Lock size={11} aria-hidden /> {tProject('project.create.vis_hint_private')}</>}
        </p>

        {error && <div className="pj-modal-error" role="alert">{error}</div>}

        <footer className="pcs-foot">
          <button type="button" className="pcs-cancel" onClick={onClose} disabled={submitting}>
            {tProject('project.btn.cancel')}
          </button>
          <button type="button" className="pcs-submit" onClick={() => void submit()} disabled={submitting}>
            {submitting ? tProject('project.btn.saving') : tProject('project.create.confirm')}
          </button>
        </footer>
      </div>
    </div>
  );
}
